import { parseDbTimestamp } from "@repo/shared";
import type { PdfGenerationLogWithDetails } from "@repo/shared/types";
import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useConfirm } from "@/hooks/use-confirm";
import { del, get, post } from "@/lib/api/_client";
import { usePdfTemplatesByFlow } from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./pdf-generation-section.i18n";

type PdfGenerationSectionProps = {
  flowInstanceId: string;
  flowId: string;
  onPdfChange?: () => void;
  isLocked?: boolean;
  /**
   * Document-archive mode for final (completed) instances: only the generated
   * documents with download — no generate form, no lock warning, no delete.
   */
  readOnly?: boolean;
};

export function PdfGenerationSection({
  flowInstanceId,
  flowId,
  onPdfChange,
  isLocked = false,
  readOnly = false,
}: PdfGenerationSectionProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { templates } = usePdfTemplatesByFlow(flowId);
  const [logs, setLogs] = useState<PdfGenerationLogWithDetails[]>([]);
  const [isPending, startTransition] = useTransition();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  // QL-439 — a flow has at most one PDF template (unique pdf_template.flowId),
  // so there is nothing to choose: the section names the template it will use.
  const template = templates[0];

  // Load logs
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setError(null);
        const logsResult = await get<PdfGenerationLogWithDetails[]>(
          `/internal/pdf-generation/logs/${flowInstanceId}`,
        );
        setLogs(logsResult);
      } catch (_err) {
        setError(t.failedToLoadTemplates);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [flowInstanceId, t.failedToLoadTemplates]);

  const handleGenerate = () => {
    if (!template) return;

    startTransition(async () => {
      try {
        setError(null);
        const result = await post<{ success: boolean; error?: string }>(
          "/internal/pdf-generation/generate",
          { templateId: template.id, flowInstanceId },
        );

        if (!result.success) {
          setError(result.error || t.failedToGeneratePdf);
          return;
        }

        // Reload logs to show the new generation
        try {
          const logs = await get<PdfGenerationLogWithDetails[]>(
            `/internal/pdf-generation/logs/${flowInstanceId}`,
          );
          setLogs(logs);
        } catch {
          // Ignore reload error
        }

        onPdfChange?.();
      } catch (err: any) {
        setError(err?.message || t.failedToGeneratePdf);
      }
    });
  };

  const handleDelete = async (logId: string, templateName: string) => {
    const confirmed = await confirm({
      title: t.deletePdf,
      description: t.deleteConfirmation(templateName),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) {
      return;
    }

    startTransition(async () => {
      try {
        setError(null);
        const result = await del<{ success: boolean; error?: string }>(
          `/internal/pdf-generation/logs/${logId}`,
        );

        if (!result.success) {
          setError(result.error || t.failedToDeletePdf);
          return;
        }

        // Reload logs to remove the deleted PDF
        try {
          const logs = await get<PdfGenerationLogWithDetails[]>(
            `/internal/pdf-generation/logs/${flowInstanceId}`,
          );
          setLogs(logs);
        } catch {
          // Ignore reload error
        }

        onPdfChange?.();
      } catch (err: any) {
        setError(err?.message || t.failedToDeletePdf);
      }
    });
  };

  const statusLabels: Record<string, string> = {
    completed: t.completed,
    pending: t.pending,
    generating: t.generating,
    failed: t.failed,
  };

  const getStatusBadge = (status: string) => (
    <StatusBadge status={status}>{statusLabels[status] ?? status}</StatusBadge>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t.pdfDocuments}
          </CardTitle>
          <CardDescription>{t.pdfDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!template) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t.pdfDocuments}
          </CardTitle>
          <CardDescription>{t.pdfDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t.noTemplatesConfigured}</p>
            <p className="text-sm text-muted-foreground mt-2">{t.goToSettings}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t.pdfDocuments}
        </CardTitle>
        <CardDescription>{t.pdfDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Locked warning */}
        {isLocked && !readOnly && (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
            <p className="text-sm text-warning">{t.lockedWarning}</p>
          </div>
        )}

        {/* Generate new PDF */}
        {!readOnly && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t.generateNewPdf}</h3>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            <div className="flex items-center justify-between gap-4 rounded-lg border px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t.template}</p>
                <p className="truncate text-sm font-medium">{template.name}</p>
              </div>
              <Button onClick={handleGenerate} disabled={isLocked} loading={isPending}>
                {t.generatePdfButton}
              </Button>
            </div>
          </div>
        )}

        {/* Generated PDFs list */}
        {logs.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t.generatedPdfs}</h3>
            <Table variant="data">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.template}</TableHead>
                  <TableHead>{t.status}</TableHead>
                  <TableHead>{t.generated}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">
                      {log.template?.name || t.unknownTemplate}
                    </TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {log.generatedAt ? (
                        <TimeAgoTooltip date={parseDbTimestamp(log.generatedAt)}>
                          {getRelativeTime(log.generatedAt, userLanguage)}
                        </TimeAgoTooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {log.status === "completed" && log.s3Key ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                window.open(
                                  `${process.env.BUN_PUBLIC_API_URL}/internal/pdf-generation/logs/${log.id}/download`,
                                  "_blank",
                                )
                              }
                              tooltip={t.download}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {!readOnly && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleDelete(log.id, log.template?.name || t.unknownTemplate)
                                }
                                disabled={isPending || isLocked}
                                tooltip={isLocked ? t.cannotDeleteLocked : t.delete}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        ) : log.status === "failed" ? (
                          <>
                            <span className="text-sm text-destructive mr-2">
                              {log.errorMessage || t.generationFailed}
                            </span>
                            {!readOnly && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleDelete(log.id, log.template?.name || t.unknownTemplate)
                                }
                                disabled={isPending || isLocked}
                                tooltip={isLocked ? t.cannotDeleteLocked : t.delete}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">{t.processing}</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Missing product documents — the PDF was generated, but these
                regulatory documents could not be appended. */}
            {logs
              .filter((log) => log.status === "completed" && log.missingDocuments?.length)
              .map((log) => (
                <div
                  key={`missing-${log.id}`}
                  className="bg-warning/10 border border-warning/30 rounded-lg p-4 space-y-1"
                >
                  <p className="text-sm text-warning font-medium">
                    {log.template?.name ? `${log.template.name}: ` : ""}
                    {t.missingDocumentsWarning}
                  </p>
                  <ul className="text-sm text-warning list-disc pl-5">
                    {log.missingDocuments?.map((doc) => {
                      const typeLabel = localize(doc.documentTypeNameI18n);
                      return (
                        <li key={`${log.id}-${doc.isin ?? doc.productName}-${typeLabel}`}>
                          {doc.productName}
                          {doc.isin ? ` (${doc.isin})` : ""} — {typeLabel}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
          </div>
        )}

        {logs.length === 0 && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">{t.noPdfsGenerated}</p>
          </div>
        )}
      </CardContent>
      <ConfirmationDialog />
    </Card>
  );
}
